import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import { Input, Select, Textarea } from "@/components/base/Field";
import PhotoUploader from "@/components/base/PhotoUploader";
import { useToast } from "@/components/base/Toast";
import { createGuardian, createStudent, linkGuardian, updateStudent } from "@/services/studentService";
import type { GuardianRelation, Student } from "@/types";
import { GUARDIAN_RELATIONS, GUARDIAN_RELATION_LABEL } from "@/utils/enrollment";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: Student | null;
}

const empty = {
  photo: "",
  lastName: "",
  firstName: "",
  gender: "M",
  birthDate: "",
  birthPlace: "",
  address: "",
  phone: "",
  notes: "",
  guardianNom: "",
  guardianPrenom: "",
  guardianTelephone: "",
  guardianProfession: "",
  relation: "PERE" as GuardianRelation,
};

export default function StudentFormModal({ open, onClose, onSaved, editing }: Props) {
  const { notify } = useToast();
  const [form, setForm] = useState({ ...empty });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...empty,
        photo: editing.photo ?? "",
        lastName: editing.lastName,
        firstName: editing.firstName,
        gender: editing.gender,
        birthDate: editing.birthDate,
        birthPlace: editing.birthPlace,
        address: editing.address,
        phone: editing.phone,
        notes: editing.notes,
      });
    } else {
      setForm({ ...empty });
    }
  }, [open, editing]);

  const set = (key: keyof typeof empty, value: string) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    if (!form.lastName.trim() || !form.firstName.trim()) {
      notify("Le nom et le prénom de l'élève sont obligatoires.", "error");
      return;
    }
    setBusy(true);
    if (editing) {
      await updateStudent(editing.id, {
        photo: form.photo,
        lastName: form.lastName.trim(),
        firstName: form.firstName.trim(),
        gender: form.gender as Student["gender"],
        birthDate: form.birthDate,
        birthPlace: form.birthPlace,
        address: form.address,
        phone: form.phone,
        notes: form.notes,
      });
      notify("Dossier élève mis à jour.");
    } else {
      const student = await createStudent({
        lastName: form.lastName.trim(),
        firstName: form.firstName.trim(),
        gender: form.gender as Student["gender"],
        birthDate: form.birthDate,
        birthPlace: form.birthPlace,
        address: form.address,
        phone: form.phone,
        photo: form.photo,
        notes: form.notes,
      });
      if (form.guardianNom.trim() && form.guardianPrenom.trim()) {
        const guardian = await createGuardian({
          lastName: form.guardianNom.trim(),
          firstName: form.guardianPrenom.trim(),
          phone: form.guardianTelephone,
          address: form.address,
          profession: form.guardianProfession,
        });
        await linkGuardian({
          studentId: student.id,
          guardianId: guardian.id,
          relation: form.relation,
          isPrimary: true,
        });
      }
      notify(`Dossier de ${student.firstName} ${student.lastName} créé (matricule ${student.matricule}).`);
    }
    setBusy(false);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier le dossier élève" : "Nouvel élève — dossier permanent"}
      subtitle={editing ? `Matricule ${editing.matricule}` : "Le dossier permanent ne dépend pas de l'année scolaire"}
      icon="ri-user-add-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy}>
            {busy ? "Enregistrement..." : editing ? "Enregistrer" : "Créer le dossier"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
          <PhotoUploader
            value={form.photo}
            onChange={(v) => setForm((p) => ({ ...p, photo: v }))}
            lastName={form.lastName}
            firstName={form.firstName}
            label="Photo de l'élève"
            hint="PNG, JPG ou WEBP · réduite automatiquement"
            size="md"
          />
        </div>

        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Identité de l'élève
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Nom" required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
            <Input label="Prénom" required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
            <Select label="Sexe" value={form.gender} onChange={(e) => set("gender", e.target.value)}>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </Select>
            <Input label="Date de naissance" type="date" value={form.birthDate} onChange={(e) => set("birthDate", e.target.value)} />
            <Input label="Lieu de naissance" value={form.birthPlace} onChange={(e) => set("birthPlace", e.target.value)} />
            <Input label="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <Input label="Adresse" value={form.address} onChange={(e) => set("address", e.target.value)} wrapperClass="sm:col-span-2" />
            <Textarea label="Informations complémentaires" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} wrapperClass="sm:col-span-2" />
          </div>
        </div>

        {!editing && (
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Parent / Responsable principal
              </p>
              <Badge tone="secondary" icon="ri-user-heart-line">
                Optionnel
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Nom du responsable" value={form.guardianNom} onChange={(e) => set("guardianNom", e.target.value)} />
              <Input label="Prénom du responsable" value={form.guardianPrenom} onChange={(e) => set("guardianPrenom", e.target.value)} />
              <Input label="Téléphone" value={form.guardianTelephone} onChange={(e) => set("guardianTelephone", e.target.value)} />
              <Input label="Profession" value={form.guardianProfession} onChange={(e) => set("guardianProfession", e.target.value)} />
              <Select label="Relation avec l'élève" value={form.relation} onChange={(e) => set("relation", e.target.value)}>
                {GUARDIAN_RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {GUARDIAN_RELATION_LABEL[r]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {!editing && (
          <Badge tone="accent" icon="ri-information-line">
            L'inscription à une classe et les frais se font dans le module « Inscriptions ».
          </Badge>
        )}
      </div>
    </Modal>
  );
}